/**
 * ComposeTemplates — FIKET-156 조립 템플릿 목록
 *
 * 조립(composition) 모델 진입 전용 화면. 템플릿 목록에서 compositionShell이 있는
 * 템플릿만 골라 슬롯 수·조립 여부와 함께 보여주고, 조립 편집 화면으로 보낸다.
 * 신규 API 없음 — GET /api/admin/templates의 hasShell·slotCount·usesComposition 사용.
 */
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useApiFetch } from '@/hooks/useApiFetch'
import { apiFetch } from '@/lib/api'
import { useToast } from '../components/ToastContainer'
import {
  Badge, Button, Table, Alert, EmptyState, Spinner, Modal, Input, Select,
  formatDate, type TableColumn,
} from '@/ui'
import styles from './ComposeTemplates.module.css'

interface ComposeTemplateRow {
  id: string
  name: string
  slug: string
  category: string
  isActive: boolean
  updatedAt: string
  hasShell?: boolean
  slotCount?: number
  usesComposition?: boolean
}

const CATEGORY_KR: Record<string, string> = {
  CLASSIC: '클래식', MODERN: '모던', VINTAGE: '빈티지',
  FLORAL: '플로럴', MINIMAL: '미니멀', ELEGANT: '엘레건트', NATURAL: '내추럴',
}

const CATEGORY_OPTIONS = Object.entries(CATEGORY_KR).map(([value, label]) => ({ value, label }))

/** slug 규칙 — 소문자·숫자·하이픈만(서버 slug 컬럼 64자 제한과 별도로 UI에서 선검증) */
const SLUG_RE = /^[a-z0-9-]+$/

export function ComposeTemplates() {
  const navigate = useNavigate()
  const { addToast } = useToast()
  const { data, error, isLoading } = useApiFetch<{ templates: ComposeTemplateRow[] }>(
    '/api/admin/templates',
  )

  const all = data?.templates ?? []
  const composable = useMemo(() => all.filter((t) => t.hasShell), [all])
  const notSeeded = all.length - composable.length

  // ── 신규 조립 템플릿 생성 폼 ──
  const [createOpen, setCreateOpen] = useState(false)
  const [form, setForm] = useState({ name: '', slug: '', category: 'CLASSIC', shellSourceTemplateId: '' })
  const [formError, setFormError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  const shellOptions = useMemo(
    () => composable.map((t) => ({ value: t.id, label: `${t.name} (${t.slug}) · 슬롯 ${t.slotCount ?? 0}개` })),
    [composable],
  )

  const openCreate = () => {
    setForm({ name: '', slug: '', category: 'CLASSIC', shellSourceTemplateId: shellOptions[0]?.value ?? '' })
    setFormError(null)
    setCreateOpen(true)
  }

  const submitCreate = async () => {
    const name = form.name.trim()
    const slug = form.slug.trim()
    if (!name) return setFormError('템플릿 이름을 입력하세요')
    if (!slug) return setFormError('slug을 입력하세요')
    if (!SLUG_RE.test(slug)) return setFormError('slug은 소문자·숫자·하이픈만 사용할 수 있습니다')
    if (all.some((t) => t.slug === slug)) return setFormError('이미 사용 중인 slug입니다')
    if (!form.shellSourceTemplateId) return setFormError('골격 원본 템플릿을 선택하세요')

    setCreating(true)
    setFormError(null)
    try {
      const created = await apiFetch<{ id: string }>('/api/admin/templates/compose-new', {
        method: 'POST',
        body: JSON.stringify({ name, slug, category: form.category, shellSourceTemplateId: form.shellSourceTemplateId }),
      })
      addToast({ type: 'success', message: '조립 템플릿을 생성했습니다. 슬롯을 채워 저장하세요.' })
      setCreateOpen(false)
      // 생성 직후엔 슬롯이 비어 있으므로 곧바로 조립 화면으로 보낸다
      navigate(`/wedding/templates/${created.id}/compose`)
    } catch (e: any) {
      setFormError(e.message ?? '생성에 실패했습니다')
    } finally {
      setCreating(false)
    }
  }

  const columns: TableColumn<ComposeTemplateRow>[] = useMemo(
    () => [
      {
        key: 'name',
        label: '템플릿',
        render: (t) => (
          <div className={styles.nameCell}>
            <span className={styles.name}>{t.name}</span>
            <span className={styles.slug}>{t.slug}</span>
          </div>
        ),
      },
      {
        key: 'category',
        label: '카테고리',
        width: '110px',
        render: (t) => CATEGORY_KR[t.category] ?? t.category,
      },
      {
        key: 'slotCount',
        label: '슬롯',
        width: '80px',
        align: 'right',
        render: (t) => `${t.slotCount ?? 0}개`,
      },
      {
        key: 'usesComposition',
        label: '조립 상태',
        width: '120px',
        render: (t) =>
          t.usesComposition
            ? <Badge color="green">조립 모델</Badge>
            : <Badge color="gray">미전환</Badge>,
      },
      {
        key: 'isActive',
        label: '노출',
        width: '90px',
        render: (t) => (t.isActive ? <Badge color="blue">활성</Badge> : <Badge color="yellow">비활성</Badge>),
      },
      {
        key: 'updatedAt',
        label: '수정일',
        width: '110px',
        render: (t) => formatDate(t.updatedAt),
      },
      {
        key: 'actions',
        label: '',
        width: '150px',
        align: 'right',
        render: (t) => (
          <div className={styles.actions}>
            <Button size="sm" onClick={() => navigate(`/wedding/templates/${t.id}/compose`)}>
              조립
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate(`/wedding/templates/${t.id}`)}>
              편집
            </Button>
          </div>
        ),
      },
    ],
    [navigate],
  )

  if (isLoading) return <div className={styles.loading}><Spinner /></div>

  if (error) {
    return (
      <div className={styles.page}>
        <EmptyState title="목록을 불러오지 못했습니다" description={error} />
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h2 className={styles.title}>조립 템플릿</h2>
        <div className={styles.headerActions}>
          <Button onClick={openCreate} disabled={shellOptions.length === 0}>
            + 조립 템플릿 생성
          </Button>
          <Button variant="outline" onClick={() => navigate('/wedding/templates')}>
            전체 템플릿
          </Button>
        </div>
      </div>

      <Alert variant="info">
        섹션 라이브러리의 섹션을 shell 슬롯에 끼워 템플릿을 조립합니다. 저장하면 해당 템플릿은
        조립 모델로 전환되고 렌더 소스(liquidHtml)를 덮어씁니다.
        {notSeeded > 0 && ` shell이 없는 템플릿 ${notSeeded}개는 여기 표시되지 않습니다(시드 필요).`}
      </Alert>

      {composable.length === 0 ? (
        <EmptyState
          title="조립 가능한 템플릿이 없습니다"
          description="compositionShell이 있는 템플릿만 조립할 수 있습니다. 시드(seed-t7-sections)로 shell을 채워주세요."
        />
      ) : (
        <Table
          columns={columns}
          data={composable}
          rowKey={(t) => t.id}
          size="sm"
          hoverable
        />
      )}

      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="조립 템플릿 생성" size="md">
        <div className={styles.form}>
          <Alert variant="info">
            골격(shell)은 기존 템플릿에서 복사합니다. 골격에는 head·표지(cover)·모달·BGM·컨트롤러
            스크립트가 들어 있어 새로 만들 수 없고, 섹션 조합과 전역 CSS만 새로 고르게 됩니다.
            생성 직후엔 슬롯이 비어 있어 비활성 상태이며, 전 슬롯을 채워 저장하면 조립 모델이 됩니다.
          </Alert>

          <Select
            label="골격 원본"
            required
            options={shellOptions}
            value={form.shellSourceTemplateId}
            onChange={(e) => setForm((f) => ({ ...f, shellSourceTemplateId: e.target.value }))}
          />
          <Input
            label="템플릿 이름"
            required
            value={form.name}
            placeholder="예: T7 파생 - 미니멀"
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
          <Input
            label="slug"
            required
            value={form.slug}
            placeholder="예: t7-minimal"
            helperText="소문자·숫자·하이픈만. 청첩장 URL과 캐시 키에 사용됩니다."
            onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
          />
          <Select
            label="카테고리"
            options={CATEGORY_OPTIONS}
            value={form.category}
            onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
          />

          {formError && <Alert variant="danger">{formError}</Alert>}

          <div className={styles.formActions}>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={creating}>
              취소
            </Button>
            <Button onClick={submitCreate} disabled={creating}>
              {creating ? '생성 중...' : '생성하고 조립하기'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
